import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Replace with your actual API instance
import { PaginationInfo } from "../../types/api";

interface SalesCustomersContextType {
  customers: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchCustomers: (page?: number, perPage?: number) => Promise<void>;
  addCustomer: (customer: any) => Promise<void>;
  editCustomer: (updatedCustomer: any) => Promise<void>;
  deleteCustomer: (id: number | string) => Promise<void>;
}

export const SalesCustomersContext = createContext<
  SalesCustomersContextType | undefined
>(undefined);

interface SalesCustomersProviderProps {
  children: ReactNode;
}

const toCustomerPayload = (customer: any) => ({
  customerName: customer.name ?? customer.customerName ?? customer.customer_name ?? customer.company_name ?? "Customer",
  contactPerson: customer.contact_person ?? customer.contactPerson ?? customer.name ?? customer.company_name ?? "N/A",
  phoneNumber: customer.phone ?? customer.phoneNumber ?? customer.phone_number ?? "0000000000",
  email: customer.email,
  address: customer.address || customer.city || "N/A",
  taxNo: customer.gst_number ?? customer.taxNo ?? customer.tax_no ?? "N/A",
  stateCode: customer.state ?? customer.stateCode ?? customer.state_code ?? "N/A",
  pincode: customer.pincode || null,
  leadSource: customer.lead_source || customer.leadSource || null,
  status: customer.status || undefined,
  notes: customer.notes || null,
});

export const useSalesCustomersContext = (): SalesCustomersContextType => {
  const context = useContext(SalesCustomersContext);
  if (!context) {
    throw new Error(
      "useSalesCustomersContext must be used within SalesCustomersProvider",
    );
  }
  return context;
};

const SalesCustomersProvider: React.FC<SalesCustomersProviderProps> = ({
  children,
}) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all customers with pagination
  const fetchCustomers = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/customers?${params.toString()}`);
      
      // Handle paginated response structure
      let customersData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          customersData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.meta?.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          customersData = responseData.data;
          paginationData = responseData.meta?.pagination || responseData.pagination;
        }
      } else if (Array.isArray(responseData)) {
        customersData = responseData;
      }
      
      setCustomers(Array.isArray(customersData) ? customersData : []);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      setCustomers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new customer
  const addCustomer = async (customer: any): Promise<void> => {
    try {
      const response = await api.post("/customers", toCustomerPayload(customer));
      setCustomers((prev) => [...prev, response.data?.data || response.data]);
    } catch (error: any) {
      console.error("Failed to add customer:", error);
      throw error.response?.data || error;
    }
  };

  // Edit an existing customer
  const editCustomer = async (updatedCustomer: any): Promise<void> => {
    try {
      const response = await api.put(`/customers/${updatedCustomer.id}`, toCustomerPayload(updatedCustomer));
      const updatedCustomerData = response.data?.data || response.data;
      setCustomers((prev) =>
        prev.map((customer) =>
          customer.id === updatedCustomer.id ? updatedCustomerData : customer
        )
      );
    } catch (error: any) {
      console.error("Failed to edit customer:", error);
      throw error.response?.data || error;
    }
  };

  // Delete a customer
  const deleteCustomer = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/customers/${id}`);
      setCustomers((prev) => prev.filter((customer) => customer.id !== id));
    } catch (error: any) {
      console.error("Failed to delete customer:", error);
      throw error.response?.data || error;
    }
  };

  useEffect(() => {
    fetchCustomers(1, 15);
  }, [fetchCustomers]);

  return (
    <SalesCustomersContext.Provider
      value={{
        customers,
        pagination,
        loading,
        fetchCustomers,
        addCustomer,
        editCustomer,
        deleteCustomer,
      }}
    >
      {children}
    </SalesCustomersContext.Provider>
  );
};

export default SalesCustomersProvider;
